import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, History, CreditCard, User, Send, Globe, Landmark, Zap, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";

const navItems = [
  { path: "/dashboard", icon: Home, label: "Home" },
  { path: "/transactions", icon: History, label: "History" },
  { path: "CENTER_FAB", icon: Send, label: "Transfer" },
  { path: "/cards", icon: CreditCard, label: "Cards" },
  { path: "/profile", icon: User, label: "Profile" },
];

const transferMethods = [
  { label: "International Transfer", icon: Globe, path: "/transfer/international", color: "text-slate-900", bg: "bg-slate-100", sub: "Global wire transfer" },
  { label: "Local Transfer", icon: Send, path: "/transfer/local", color: "text-emerald-600", bg: "bg-emerald-50", sub: "Instant local transfer" },
  { label: "Same Bank Transfer", icon: Landmark, path: "/transfer/internal", color: "text-indigo-600", bg: "bg-indigo-50", sub: "Zero fee transfer" },
  { label: "Crypto Transfer", icon: Landmark, path: "/transfer/crypto", color: "text-orange-600", bg: "bg-orange-50", sub: "Network fee applies" },
];

export const MobileNav = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden" data-testid="mobile-nav">
      <div className="bg-white/80 backdrop-blur-2xl border-t border-slate-100 pb-safe-area-inset-bottom">
        <div className="flex items-center justify-around h-20 px-2 relative">
          
          {navItems.map((item) => {
            if (item.path === "CENTER_FAB") {
              return (
                <Sheet key="fab" open={isOpen} onOpenChange={setIsOpen}>
                  <SheetTrigger asChild>
                    <div className="relative -top-7 px-2 cursor-pointer group">
                      <div className="w-16 h-16 bg-slate-900 rounded-[2rem] flex flex-col items-center justify-center shadow-xl shadow-slate-900/20 text-white active:scale-90 transition-all duration-200 border-4 border-white">
                        <item.icon className={cn("w-6 h-6 mb-0.5 transition-transform", isOpen && "rotate-45")} />
                      </div>
                      <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-black text-slate-900 uppercase tracking-tighter">
                        {item.label}
                      </span>
                    </div>
                  </SheetTrigger>
                  
                  <SheetContent side="bottom" className="rounded-t-[3rem] border-t-0 p-6 bg-white/95 backdrop-blur-2xl px-8">
                    <div className="flex justify-between items-center mb-8">
                      <SheetTitle className="font-black text-2xl text-slate-900 tracking-tight">Transfer Money</SheetTitle>
                      <button onClick={() => setIsOpen(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <X size={16} className="text-slate-500" />
                      </button>
                    </div>

                    <div className="grid gap-4 mb-8">
                      {transferMethods.map((method) => (
                        <NavLink
                          key={method.label}
                          to={method.path}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-4 p-4 rounded-[2rem] bg-white border border-slate-100 shadow-sm active:scale-[0.98] transition-all group"
                        >
                          <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:rotate-6", method.bg)}>
                            <method.icon className={cn("w-6 h-6", method.color)} strokeWidth={2.5} />
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-slate-900 text-xs">{method.label}</p>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{method.sub}</p>
                          </div>
                          <Zap size={16} className="text-slate-200 group-hover:text-amber-400 transition-colors" />
                        </NavLink>
                      ))}
                    </div>
                  </SheetContent>
                </Sheet>
              );
            }

            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  "relative flex flex-col items-center justify-center flex-1 h-full transition-all duration-300",
                  isActive ? "text-slate-900" : "text-slate-400"
                )}
              >
                <div className="relative flex flex-col items-center">
                  <item.icon className={cn("w-6 h-6 transition-transform duration-300", isActive ? "scale-110 stroke-[2.5]" : "scale-100 stroke-[2]")} />
                  <span className={cn("text-[9px] mt-1 font-bold tracking-tight transition-colors uppercase", isActive ? "text-slate-900" : "text-slate-400")}>
                    {item.label}
                  </span>
                </div>
                {isActive && <div className="absolute top-2 w-1 h-1 bg-slate-900 rounded-full" />}
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
};